/**
 * Intelligence Prism Data Transformation (DC-1368)
 *
 * Transforms raw API data into the PrismData visualization model.
 * Extracted from IntelligencePrism.tsx.
 */

import type {
  StationId,
  Priority,
  StationSource,
  MetricSeverity,
  Station,
  ValueDriver,
  ActionPlanTask,
  LineageSummary,
  PrismData,
  RawRecommendation,
  OrgRecommendation,
  BridgeRecommendation,
  ActionPlanTaskApi,
  RawRecsApiResponse,
  BridgeApiResponse,
  ActionPlanApiResponse,
  OrgRecsApiResponse,
} from './types';
import { STATION_META } from './constants';
import { getDriverType } from './helpers';
import { hasValue } from '../../types/actionPlanValueProvenance';

// ============================================================================
// STATION ID NORMALIZATION
// ============================================================================

/** Maps various API formats to canonical StationId values */
export const normalizeStationId = (rawId: string | undefined): StationId | null => {
  if (!rawId) return null;
  const id = rawId.toLowerCase().trim();

  // Org-level sources → org_context
  if (id === 'document' || id === 'documents' || id === 'governance_documents' ||
      id === 'org_document' || id === 'org_documents' ||
      id === 'interview' || id === 'interviews' || id === 'organizational_interviews' ||
      id === 'org_interview' || id === 'org_interviews' ||
      id === 'org_context' || id === 'organization' || id === 'org') {
    return 'org_context';
  }
  if (id === 'data_quality' || id === 'dataquality' || id === 'dq') return 'data_quality';
  if (id === 'live_checks' || id === 'livechecks' || id === 'fair' || id === 'fair_assessment' || id === 'fair_checks') return 'live_checks';
  if (id === 'ml_readiness' || id === 'mlreadiness' || id === 'ml' || id === 'machine_learning') return 'ml_readiness';
  if (id === 'ontology_inference' || id === 'ontologyinference' || id === 'ontology' || id === 'semantic' || id === 'semantic_interop') return 'ontology_inference';

  console.warn(`[Prism] Unknown station_id: "${rawId}" - not mapped`);
  return null;
};

// ============================================================================
// SEVERITY & SUMMARY HELPERS
// ============================================================================

/** DC-990: Derive severity from recommendations */
const deriveSeverity = (
  stationId: StationId,
  count: number,
  rawRecs: RawRecommendation[]
): MetricSeverity => {
  const hasCritical = rawRecs.some((rec: RawRecommendation) => {
    const normalizedId = rec.station_id?.toLowerCase();
    const matchesStation = normalizedId === stationId ||
      (stationId === 'data_quality' && (normalizedId === 'dataquality' || normalizedId === 'dq')) ||
      (stationId === 'ml_readiness' && (normalizedId === 'mlreadiness' || normalizedId === 'ml')) ||
      (stationId === 'ontology_inference' && (normalizedId === 'ontologyinference' || normalizedId === 'ontology'));
    return matchesStation && rec.priority === 'critical';
  });
  if (hasCritical) return 'critical';
  if (count >= 5) return 'warning';
  if (count >= 3) return 'info';
  return 'success';
};

/** DC-990: Generate educational summary from recommendations */
const generateSummary = (stationId: StationId, count: number): string => {
  const summaries: Record<StationId, (n: number) => string> = {
    data_quality: (n) => n === 1
      ? 'One data quality issue identified requiring attention.'
      : `${n} data quality findings identified. Review recommended actions to improve data integrity.`,
    live_checks: (n) => n === 1
      ? 'One FAIR principle improvement opportunity found.'
      : `${n} FAIR principle gaps identified. Addressing these will improve data reusability.`,
    org_context: (n) => n === 1
      ? 'One organizational insight captured from governance review.'
      : `${n} organizational insights captured from documents and interviews.`,
    ml_readiness: (n) => n === 1
      ? 'One ML readiness factor needs attention before model training.'
      : `${n} ML readiness factors identified. Review before proceeding with model development.`,
    ontology_inference: (n) => n === 1
      ? 'One ontology mapping recommendation found.'
      : `${n} ontology mapping opportunities identified for improved semantic interoperability.`,
  };
  return summaries[stationId]?.(count) || `${count} findings identified.`;
};

// ============================================================================
// MAIN TRANSFORM
// ============================================================================

/** DC-871: Transform raw API data into the PrismData visualization model */
export function transformToPrismData(
  rawRecsData: RawRecsApiResponse | null,
  bridgeData: BridgeApiResponse | null,
  actionPlanData: ActionPlanApiResponse | null,
  orgRecsData?: OrgRecsApiResponse | null,
  lineageData?: LineageSummary | null
): PrismData {
  const actionPlan = actionPlanData?.action_plan || {};
  const tasks = actionPlan?.tasks || [];
  const metadata = actionPlan?.metadata || {};
  const bridgeRecs = bridgeData?.recommendations || [];
  const rawRecs = rawRecsData?.recommendations || [];
  const orgRecs = orgRecsData?.recommendations || [];

  // =====================================================
  // STEP 1: Count RAW FINDINGS per station
  // =====================================================
  const journeyFindings: Record<StationId, number> = {
    data_quality: 0, live_checks: 0, org_context: 0, ml_readiness: 0, ontology_inference: 0,
  };
  const orgFindings: Record<StationId, number> = {
    data_quality: 0, live_checks: 0, org_context: 0, ml_readiness: 0, ontology_inference: 0,
  };

  rawRecs.forEach((rec: RawRecommendation) => {
    const stationId = normalizeStationId(rec.station_id);
    if (stationId && journeyFindings[stationId] !== undefined) {
      journeyFindings[stationId]++;
    }
  });

  console.log('[Prism] Org Recs to process:', orgRecs.length);
  orgRecs.forEach((rec: OrgRecommendation, idx: number) => {
    const sourceRec = rec.source_recommendations?.[0];
    const rawStationId = sourceRec?.station_id;
    const stationId = normalizeStationId(rawStationId);

    console.log(`[Prism] Org Rec ${idx}: raw="${rawStationId}" → normalized="${stationId}" | title="${rec.title?.slice(0,40)}"`);

    if (stationId && orgFindings[stationId] !== undefined) {
      orgFindings[stationId]++;
    } else {
      console.warn(`[Prism] Org Rec ${idx}: stationId "${rawStationId}" could not be normalized`);
    }
  });

  // =====================================================
  // STEP 2: Build LINEAGE from Bridge + Org source_recommendations
  // =====================================================
  const lineage = new Map<string, Set<StationId>>();

  const addToLineage = (driverName: string, stationId: StationId) => {
    if (!lineage.has(driverName)) {
      lineage.set(driverName, new Set());
    }
    lineage.get(driverName)!.add(stationId);
  };

  bridgeRecs.forEach((rec: BridgeRecommendation) => {
    const driverName = rec.pistoia_driver?.driver_name || rec.title;
    rec.source_recommendations?.forEach((sr: { station_id?: string; recommendation_id?: string; contribution?: string }) => {
      const stationId = normalizeStationId(sr.station_id);
      if (stationId) {
        addToLineage(driverName, stationId);
      }
    });
  });

  orgRecs.forEach((rec: OrgRecommendation) => {
    const driverName = rec.pistoia_driver?.driver_name;
    if (driverName) {
      addToLineage(driverName, 'org_context');
    }
  });

  console.log('[Prism] Lineage built:', Array.from(lineage.entries()).map(([k, v]) => `${k}: [${Array.from(v).join(',')}]`));

  // =====================================================
  // STEP 3: Group TASKS by Pistoia driver for VALUE calculation
  // =====================================================
  const driverMap: Record<string, {
    name: string;
    value: number;
    taskCount: number;
    /**
     * Tasks under this driver that DO have a value (S4 follow-up).
     *
     * Needed to tell "$0 because everything was measured at zero" from "$0 because
     * nothing was priced". Without it, `value === 0` is ambiguous and the subtotal reads
     * as a real figure when it is the sum of an empty set.
     */
    pricedTaskCount: number;
    /** Tasks in this driver with no value — `value` is a partial sum when > 0. */
    unpricedTaskCount: number;
    stations: Set<StationId>;
    priority: Priority;
  }> = {};

  const ensureDriver = (driverName: string, priority: Priority = 'optimization') => {
    if (!driverMap[driverName]) {
      driverMap[driverName] = {
        name: driverName,
        value: 0,
        taskCount: 0,
        pricedTaskCount: 0,
        unpricedTaskCount: 0,
        stations: new Set(),
        priority,
      };
    }
  };

  // Add action plan tasks (primary source of truth for task count and value)
  tasks.forEach((task: ActionPlanTaskApi) => {
    /*
     * CANONICAL DRIVER IDENTITY FIRST (S4 follow-up).
     *
     * The driver used to be read only from `value_calculation`, which the generator
     * populates only when a value succeeds. So every unpriced task was grouped under
     * 'Unknown' — the work existed, its driver was known, and the Prism said otherwise.
     *
     * `task.pistoia_driver` is now assigned at the generation boundary, before any value
     * is attempted, and is preferred here. The `value_calculation` forms remain as
     * fallbacks for plans persisted before that field existed.
     */
    const vc = task.value_calculation || {};
    const rawDriver = vc.pistoia_driver;
    const driverName =
      task.pistoia_driver?.driver_name ||
      task.value_withheld?.driver_name ||
      (typeof rawDriver === 'object' && rawDriver?.driver_name
        ? rawDriver.driver_name
        : typeof rawDriver === 'string'
          ? rawDriver
          : 'Unknown');
    const priority: Priority = task.priority === 'critical' ? 'critical' :
                                task.priority === 'high' ? 'warning' : 'optimization';

    ensureDriver(driverName, priority);
    // `task.value_usd || 0` added a `Quantified` OBJECT to a number, giving NaN,
    // which then poisoned the whole driver total (S4d follow-up). An unpriced
    // task now contributes nothing and is counted separately — adding it as zero
    // would understate the driver and present the result as complete.
    const taskValue = hasValue(task.value_usd) ? task.value_usd.value : null;
    if (taskValue !== null) {
      driverMap[driverName].value += taskValue;
      driverMap[driverName].pricedTaskCount += 1;
    } else {
      driverMap[driverName].unpricedTaskCount += 1;
    }
    driverMap[driverName].taskCount++;

    const stationsFromLineage = lineage.get(driverName) || new Set<StationId>();
    stationsFromLineage.forEach(s => driverMap[driverName].stations.add(s));
  });

  // STEP 3b: Create/enrich value drivers from Bridge AI recommendations.
  // When no action plan exists, Bridge AI financial_summary is the value source.
  // When action plan exists, Bridge AI only adds station lineage to existing drivers.
  const hasActionPlanTasks = tasks.length > 0;

  bridgeRecs.forEach((rec: BridgeRecommendation) => {
    const driverName = rec.pistoia_driver?.driver_name || rec.title;
    if (!driverName) return;

    const priority: Priority = rec.priority === 'critical' ? 'critical' :
                                rec.priority === 'high' ? 'warning' : 'optimization';

    if (!hasActionPlanTasks && rec.financial_summary) {
      // No action plan yet — use Bridge AI financial data as value source
      ensureDriver(driverName, priority);
      driverMap[driverName].value += rec.financial_summary.net_roi_usd || 0;
      driverMap[driverName].pricedTaskCount += 1;
      driverMap[driverName].taskCount++;
    }

    // Add station lineage from source_recommendations
    if (driverMap[driverName]) {
      rec.source_recommendations?.forEach((sr: { station_id?: string; recommendation_id?: string; contribution?: string }) => {
        const stationId = normalizeStationId(sr.station_id);
        if (stationId) {
          driverMap[driverName].stations.add(stationId);
        }
      });

      rec.affected_stations?.forEach((stationId: string) => {
        const normalizedId = normalizeStationId(stationId);
        if (normalizedId) {
          driverMap[driverName].stations.add(normalizedId);
        }
      });
    }
  });

  // =====================================================
  // STEP 4: Build VALUE DRIVERS array
  // =====================================================
  const valueDrivers: ValueDriver[] = Object.entries(driverMap)
    /*
     * A DRIVER IS KEPT IF IT HAS TASKS. Full stop (S4d follow-up).
     *
     * `value > 0` alone dropped any driver whose tasks were all unpriced, so the
     * work existed and the reader could not see it. `value > 0 || unpriced > 0`
     * fixed that case and left a subtler one the operator caught: a driver that is
     * FULLY PRICED at a genuine zero has `value === 0` and `unpricedTaskCount === 0`,
     * so it still vanished — and because its tasks were counted elsewhere, the
     * aggregate then read "unavailable" for a plan that was completely priced.
     *
     * A measured zero is a result. `taskCount > 0` is the only condition that
     * distinguishes "this driver has work" from "this driver does not exist", which
     * is the only question this filter should be asking.
     */
    .filter(([_, data]) => data.taskCount > 0)
    .map(([name, data]) => ({
      id: name,
      name: data.name,
      value: data.value,
      taskCount: data.taskCount,
      pricedTaskCount: data.pricedTaskCount,
      unpricedTaskCount: data.unpricedTaskCount,
      driverType: getDriverType(data.name),
      sourceStations: Array.from(data.stations),
      priority: data.priority,
    }))
    .sort((a, b) => b.value - a.value);

  // =====================================================
  // STEP 5: Build STATIONS array
  // =====================================================
  const stations: Station[] = [];

  Object.entries(STATION_META).forEach(([id, meta]) => {
    const stationId = id as StationId;
    const journeyCount = journeyFindings[stationId] || 0;
    const orgCount = orgFindings[stationId] || 0;
    const totalCount = journeyCount + orgCount;

    if (totalCount > 0) {
      let source: StationSource = 'journey';
      if (journeyCount > 0 && orgCount > 0) {
        source = 'merged';
      } else if (orgCount > 0) {
        source = 'org';
      }

      stations.push({
        id: stationId,
        name: meta.name,
        icon: meta.icon,
        findingsCount: totalCount,
        status: 'completed' as const,
        source,
        severity: deriveSeverity(stationId, totalCount, rawRecs),
        educational_summary: generateSummary(stationId, totalCount),
      });
    }
  });

  // =====================================================
  // STEP 6: Build TASKS array for driver expansion
  // =====================================================
  const allTasks: ActionPlanTask[] = [];

  tasks.forEach((task: ActionPlanTaskApi) => {
    const vc = task.value_calculation || {};
    const rawDriver = vc.pistoia_driver;
    const driverName = typeof rawDriver === 'object' && rawDriver?.driver_name
      ? rawDriver.driver_name
      : (typeof rawDriver === 'string' ? rawDriver : 'Unknown');
    allTasks.push({
      id: task.id || `task-${allTasks.length}`,
      title: task.title || 'Untitled Task',
      description: task.description,
      priority: task.priority || 'medium',
      implementation_steps: task.implementation_steps,
      // The scalar, separately from provenance: this local shape feeds charts.
      value_usd: hasValue(task.value_usd) ? task.value_usd.value : undefined,
      pistoia_driver: driverName,
      source: 'journey',
      value_calculation: vc ? {
        formula_trace: vc.formula_trace,
        calculation_narrative: vc.calculation_narrative,
        base_value_usd: vc.base_value_usd,
        total_value_usd: vc.total_value_usd,
        npv_3yr_usd: vc.npv_3yr_usd,
        fair_improvements: vc.fair_improvements,
        multipliers: vc.multipliers,
        evidence_chain: vc.evidence_chain,
      } : undefined,
    });
  });

  // =====================================================
  // STEP 7: Calculate totals from ACTUAL data
  // =====================================================
  const totalFindings = stations.reduce((sum, s) => sum + s.findingsCount, 0);
  /*
   * Three figures, deliberately separate (S4d follow-up).
   *
   * `totalROI` was a single sum over the priced tasks, rendered as both
   * "Projected ROI" and "Total Projected Value". It was neither. A total that
   * silently omits the unpriced tasks is a subtotal, and calling any figure ROI
   * without a project cost is a category error.
   */
  const unpricedTasks = valueDrivers.reduce((sum, d) => sum + (d.unpricedTaskCount ?? 0), 0);
  const pricedTasks = valueDrivers.reduce((sum, d) => sum + (d.pricedTaskCount ?? 0), 0);
  /*
   * A SUBTOTAL OF NOTHING IS NOT ZERO (S4 follow-up).
   *
   * `reduce(..., 0)` over an empty or fully-unpriced set returns 0, and the UI then
   * rendered "$0 priced subtotal" — which is false. Nothing was priced, so there is no
   * subtotal, and 0 claims a measurement that was never taken. Null is the only honest
   * value when `pricedTasks === 0`.
   */
  const valuePricedSubtotal =
    pricedTasks > 0 ? valueDrivers.reduce((sum, d) => sum + d.value, 0) : null;
  // Complete only when nothing is missing AND something was priced.
  const valueComplete =
    unpricedTasks === 0 && pricedTasks > 0 ? valuePricedSubtotal : null;

  console.log('[Prism] Transform:', {
    journeyFindings: Object.entries(journeyFindings).filter(([_, v]) => v > 0).map(([k, v]) => `${k}:${v}`),
    orgFindings: Object.entries(orgFindings).filter(([_, v]) => v > 0).map(([k, v]) => `${k}:${v}`),
    totalFindings,
    bridgePriorities: bridgeRecs.length,
    tasks: tasks.length,
    stations: stations.map(s => `${s.name}:${s.findingsCount}(${s.source})`),
    drivers: valueDrivers.map(d => `${d.name}:$${d.value}←[${d.sourceStations.join(',')}]`),
    lineage: Array.from(lineage.entries()).map(([k, v]) => `${k}←[${Array.from(v).join(',')}]`),
  });

  const lineageSummary: LineageSummary | undefined = lineageData ? {
    total_events: lineageData.total_events || 0,
    by_type: lineageData.by_type || {},
    by_source: lineageData.by_source || {},
  } : undefined;

  return {
    stations,
    valueDrivers,
    recommendations: bridgeRecs,
    tasks: allTasks,
    totals: {
      findings: totalFindings,
      priorities: bridgeRecs.length,
      tasks: allTasks.length,
      valueComplete,
      valuePricedSubtotal: valueDrivers.length > 0 ? valuePricedSubtotal : null,
      unpricedTasks,
      pricedTasks,
      // `|| 60` invented a 60-week programme for any plan whose duration was
      // unknown, and did the same for a genuine zero (P0-4). Null now, and the
      // prism reports it as unavailable.
      duration: metadata.estimated_duration_weeks?.value ?? null,
    },
    lineage,
    lineageSummary,
  };
}
